<template>
    <ModalWithHeader
        v-qa="'saved-products-modal'"
        label="Saved Products"
        @close="$emit('close')"
    >
        <div class="saved-products-modal">
            <ThTitle
                class="title"
                color="var(--blue)"
                tag="DIV"
            >
                Your saved products
            </ThTitle>

            <div
                v-if="savedProducts.length === 0"
                class="empty-state"
            >
                <p class="text">You haven't saved any products yet.</p>

                <div class="notice">
                    Tap the <strong>heart icon</strong>
                    <Icon
                        icon="heart"
                        class="heart-icon"
                    />
                    on any product to add it here for later.
                </div>
            </div>

            <div
                v-else
                class="saved-products-list"
            >
                <div
                    v-for="(product, index) in savedProducts"
                    :key="index"
                    class="item"
                >
                    <div class="image-wrapper">
                        <img
                            class="image"
                            :src="product.image"
                            :alt="product.name"
                        />
                    </div>
                    <div class="product-info">
                        <h3 class="name">{{ product.name }}</h3>
                        <p class="description">
                            {{ product.description }}
                        </p>
                        <ThLink
                            :href="product.link"
                            class="view-button"
                            @click="handleProductClick(index)"
                        >
                            View Product
                        </ThLink>
                    </div>
                    <button
                        class="remove-button"
                        aria-label="Remove product"
                        @click="removeProductAndTrack(index)"
                    >
                        <Icon icon="close-bold" />
                    </button>
                </div>
            </div>
        </div>
    </ModalWithHeader>
</template>

<script lang="ts" setup>
import { useSavedProductsModal } from './useSavedProducts'
import { getWindow } from '@/helpers/globals.ts'
import { useSegment } from '@/third-party/segment.ts'

import ModalWithHeader from '@/components/layout/modal/ModalWithHeader.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import Icon from '@/components/ui/icon/Icon.vue'

defineEmits<{
    close: []
}>()

const { savedProducts, removeProduct } = useSavedProductsModal()

const { track } = useSegment()

const removeProductAndTrack = (index: number) => {
    removeProduct(index)

    const url = getWindow()?.location.href

    if (!url) {
        return
    }

    const event_name = 'remove_saved_product_modal'

    track(`custom_${event_name}`, {
        param1: 'Save for later',
        param2: event_name,
        param3: 'Modal remove button click',
        param4: url,
        url,
        event_name,
    })
}

const handleProductClick = (index: number) => {
    const product = savedProducts.value[index]
    const url = getWindow()?.location.href

    if (!url || !product) {
        return
    }

    const event_name = 'view_saved_product'

    track(`custom_${event_name}`, {
        param1: 'Save for later',
        param2: event_name,
        param3: 'Modal product click',
        param4: url,
        product_name: product.name,
        product_url: product.link,
        url,
        event_name,
    })
}
</script>

<style lang="scss" scoped>
.saved-products-modal {
    padding: 20px;
    max-width: 100%;

    .title {
        margin-bottom: 50px;
        font-size: 1.5rem;
        text-align: center;
    }

    .empty-state {
        text-align: center;
        padding: 0 0 30px 0;

        p.text {
            margin: 0 0 20px;
        }

        .notice {
            margin: 50px 0 0 0;
            padding: 20px;
            background: $stone-2;
            border: 1px solid $grey-200;
            font-size: 0.875rem;
        }

        .heart-icon {
            color: var(--slate);
            font-size: 1.2em;
            vertical-align: middle;
            margin: 0 2px 0 5px;
        }
    }

    .saved-products-list {
        width: 1140px;
        max-width: 100%;
        margin: 0 auto 60px;
        padding: 0 0 20px;
        display: flex;
        justify-content: center;
        gap: 30px 20px;
        flex-wrap: wrap;
    }

    .item {
        width: 100%;
        position: relative;

        @include min-width(520) {
            width: calc(50% - 10px);
        }

        @include min-width(768) {
            width: calc(33.333% - 15px);
        }

        @include min-width(1024) {
            width: calc(25% - 15px);
        }

        .image-wrapper {
            background: #f9f9f9;
            width: 100%;
            height: auto;
            aspect-ratio: 1/1;
            margin: 0 0 15px 0;

            .image {
                width: 100%;
                height: 100%;
                object-fit: cover;
                mix-blend-mode: darken;
            }
        }

        .name {
            font-family: $parcel-font-stack;
            color: $blue;
            font-size: 1.2rem;
            line-height: 1.2em;
            margin: 0 0 10px;
        }

        .description {
            font-size: 0.875rem;
            margin: 0 0 15px;
            min-height: 60px;
        }

        .view-button {
            font-family: $effra-font-stack;
            color: $blue;
            letter-spacing: 0.05em;
            font-size: 0.875rem;
            text-transform: uppercase;
        }

        .remove-button {
            position: absolute;
            top: 0;
            right: 0;
            width: 40px;
            height: 40px;
            background: none;
            border: none;
            font-size: 1.4rem;
            cursor: pointer;
            color: $grey-400;
            text-align: center;
            line-height: 40px;

            &:hover {
                background: $grey-200;
                color: $slate;
            }
        }
    }
}
</style>
